import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, Instagram, Send, Bot, User, ArrowRight, CheckCircle, Clock, Sparkles, Globe, Brain, Code, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { AIStatus } from './AIStatus';
import { AITeacher } from './AITeacher';

interface MessageButton {
  label: string;
  action: 'url' | 'message' | 'phone' | 'email' | 'whatsapp';
  value: string;
  style?: 'primary' | 'secondary' | 'success';
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  platform: 'instagram' | 'whatsapp' | 'web';
  timestamp: Date;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
  aiAnalysis?: {
    language: string;
    intent: string;
    sentiment: { label: string; score: number };
    confidence: number;
    processingTime: number;
  };
  buttons?: MessageButton[];
}

interface AIAnalysis {
  language: string;
  intent: string;
  sentiment: { label: string; score: number };
  entities: Array<{ type: string; value: string }>;
  confidence: number;
  suggestedResponse: string;
  contextMatches?: Array<{ question: string; answer: string; score: number }>;
  processingTime: number;
}

export const UnifiedChat: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    platform: '',
    originalMessage: ''
  });
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [language, setLanguage] = useState<'en' | 'pt'>('en');
  const [showDevMode, setShowDevMode] = useState(false);
  const [latestAnalysis, setLatestAnalysis] = useState<AIAnalysis | null>(null);
  const [isAIProcessing, setIsAIProcessing] = useState(false);

  useEffect(() => {
    // Parse handoff parameters
    const from = searchParams.get('from');
    const token = searchParams.get('token');
    const userName = searchParams.get('user') || 'Guest';
    
    if (from && token) {
      handlePlatformHandoff(from, token, userName);
    }
  }, [searchParams]);

  const handlePlatformHandoff = async (platform: string, token: string, userName: string) => {
    setUserInfo({
      name: userName,
      platform,
      originalMessage: ''
    });

    // Fetch original context from Instagram
    try {
      const response = await fetch(`/api/handoff/context?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setUserInfo(prev => ({
          ...prev,
          originalMessage: data.originalMessage
        }));
      }
    } catch (error) {
      console.error('Failed to fetch context:', error);
    }

    // Add welcome message
    const welcomeText = language === 'pt' 
      ? `Olá ${userName}! 👋 Vejo que você entrou em contato pelo ${platform}. Estou aqui para ajudá-lo instantaneamente! Como posso ajudar hoje?`
      : `Hi ${userName}! 👋 I see you reached out on ${platform}. I'm here to help you instantly! How can I assist you today?`;
    
    const welcomeMsg: Message = {
      id: Date.now().toString(),
      text: welcomeText,
      sender: 'bot',
      platform: 'web',
      timestamp: new Date(),
      buttons: [
        {
          label: language === 'pt' ? '📅 Verificar Disponibilidade' : '📅 Check Availability',
          action: 'message',
          value: language === 'pt' ? 'Quero verificar disponibilidade para o Carnaval' : 'I want to check availability',
          style: 'primary'
        },
        {
          label: language === 'pt' ? '💰 Ver Preços' : '💰 View Prices',
          action: 'message',
          value: language === 'pt' ? 'Quais são os preços das suítes?' : 'What are the suite prices?',
          style: 'secondary'
        },
        {
          label: language === 'pt' ? '🏖️ Sobre a Pousada' : '🏖️ About Us',
          action: 'message',
          value: language === 'pt' ? 'Me conte sobre a pousada' : 'Tell me about the pousada',
          style: 'secondary'
        },
        {
          label: '💬 WhatsApp Direto',
          action: 'whatsapp',
          value: '+5581999999999',
          style: 'success'
        },
        {
          label: '📸 Instagram',
          action: 'url',
          value: 'https://instagram.com/carneirosparadiso',
          style: 'secondary'
        }
      ]
    };
    
    setMessages([welcomeMsg]);
    setIsConnected(true);
    
    // Prompt for WhatsApp after 3 seconds
    setTimeout(() => {
      setShowWhatsAppPrompt(true);
    }, 3000);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      platform: 'web',
      timestamp: new Date(),
      status: 'sending'
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);
    setIsAIProcessing(true);

    try {
      // Call CloudFlare AI API
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          context: {
            sessionId: `session_${Date.now()}`,
            userName: userInfo.name,
            platform: userInfo.platform,
            conversationHistory: messages.slice(-5).map(m => m.text),
            preferredLanguage: language
          }
        })
      });

      const data = await response.json();
      
      // Store AI analysis for dev mode
      if (data.analysis) {
        setLatestAnalysis(data.analysis);
      }
      
      // Add contextual buttons based on the response
      const buttons = [];
      const msgLower = inputMessage.toLowerCase();
      
      if (msgLower.includes('reserv') || msgLower.includes('book') || msgLower.includes('disponib')) {
        buttons.push({
          label: language === 'pt' ? '📅 Fazer Reserva' : '📅 Make Booking',
          action: 'url' as const,
          value: 'https://biig.dev/booking',
          style: 'primary' as const
        });
      }
      
      if (msgLower.includes('whatsapp') || msgLower.includes('contato') || msgLower.includes('contact')) {
        buttons.push({
          label: '💬 WhatsApp',
          action: 'whatsapp' as const,
          value: '+5581999999999',
          style: 'success' as const
        });
      }
      
      if (msgLower.includes('instagram') || msgLower.includes('foto') || msgLower.includes('photo')) {
        buttons.push({
          label: '📸 Instagram',
          action: 'url' as const,
          value: 'https://instagram.com/carneirosparadiso',
          style: 'secondary' as const
        });
      }

      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: data.response || (language === 'pt' ? 'Entendo. Como mais posso ajudar?' : 'I understand. How else can I help you?'),
        sender: 'bot',
        platform: 'web',
        timestamp: new Date(),
        aiAnalysis: data.analysis,
        buttons: buttons.length > 0 ? buttons : undefined
      };
      
      setMessages(prev => [...prev.map(m => 
        m.id === userMsg.id ? { ...m, status: 'delivered' as const } : m
      ), botMsg]);
      
      // Get suggestions based on intent
      if (data.intent) {
        fetchSuggestions(data.intent);
      }
      
    } catch (error) {
      console.error('AI response error:', error);
      // Fallback to local response
      const aiResponse = await generateAIResponse(inputMessage);
      
      // Add fallback buttons
      const fallbackButtons = [];
      if (!inputMessage.toLowerCase().includes('whatsapp')) {
        fallbackButtons.push({
          label: '💬 Falar no WhatsApp',
          action: 'whatsapp' as const,
          value: '+5581999999999',
          style: 'success' as const
        });
      }
      
      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        sender: 'bot',
        platform: 'web',
        timestamp: new Date(),
        buttons: fallbackButtons.length > 0 ? fallbackButtons : undefined
      };
      
      setMessages(prev => [...prev.map(m => 
        m.id === userMsg.id ? { ...m, status: 'delivered' as const } : m
      ), botMsg]);
    } finally {
      setIsTyping(false);
      setIsAIProcessing(false);
    }
  };

  const fetchSuggestions = async (intent: string) => {
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/chat/suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ intent })
      });
      
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const generateAIResponse = async (message: string): Promise<string> => {
    const msg = message.toLowerCase();
    
    // Context-aware responses
    if (msg.includes('instagram')) {
      return "I can help you manage your Instagram inquiries! You can connect your WhatsApp for seamless communication across all platforms. Would you like to set that up?";
    }
    
    if (msg.includes('order') || msg.includes('track')) {
      return "I can help you track your order! Please provide your order number, and I'll check the status for you right away.";
    }
    
    if (msg.includes('appointment') || msg.includes('book')) {
      return "I'd be happy to help you schedule an appointment! What service are you interested in, and when would you prefer to come in?";
    }
    
    if (msg.includes('price') || msg.includes('cost')) {
      return "Let me help you with pricing information. Which specific product or service would you like to know about?";
    }
    
    if (msg.includes('help')) {
      return `I can assist you with:
• Order tracking and status
• Appointment scheduling
• Product information
• Pricing and quotes
• Technical support
• General inquiries

What would you like help with?`;
    }
    
    return "Thanks for your message! I'm here to help. Could you tell me more about what you need assistance with?";
  };

  const handleButtonClick = (button: { action: string; value: string; label: string }) => {
    switch (button.action) {
      case 'url':
        window.open(button.value, '_blank');
        break;
      case 'message':
        setInputMessage(button.value);
        setTimeout(() => sendMessage(), 100);
        break;
      case 'phone':
        window.open(`tel:${button.value}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:${button.value}`, '_blank');
        break;
      case 'whatsapp':
        const whatsappUrl = `https://wa.me/${button.value.replace(/[^0-9]/g, '')}`;
        window.open(whatsappUrl, '_blank');
        break;
    }
  };

  const connectWhatsApp = () => {
    if (!phoneNumber) {
      alert('Please enter your WhatsApp number');
      return;
    }
    
    // Generate WhatsApp link with context
    const message = encodeURIComponent(`Hi! I started chatting on ${userInfo.platform} and was redirected here for support.`);
    const whatsappUrl = `https://wa.me/15551234567?text=${message}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Update UI
    setShowWhatsAppPrompt(false);
    const connectMsg: Message = {
      id: Date.now().toString(),
      text: '✅ WhatsApp connected! You can continue our conversation there for instant support.',
      sender: 'bot',
      platform: 'whatsapp',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, connectMsg]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                <MessageCircle className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  biig.dev Support
                  <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-xs font-semibold">
                    <Sparkles size={12} />
                    AI Enhanced
                  </span>
                </h1>
                <p className="text-sm text-gray-500">
                  {userInfo.platform && (
                    <span className="flex items-center gap-1">
                      Connected from <Instagram size={14} /> {userInfo.platform}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Language toggle"
              >
                <Globe size={16} />
                <span className="text-sm font-medium">
                  {language === 'en' ? 'EN' : 'PT-BR'}
                </span>
              </button>
              
              {/* Dev Mode Toggle */}
              <button
                onClick={() => setShowDevMode(!showDevMode)}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Dev mode toggle"
              >
                <Code size={16} />
                <span className="text-sm font-medium">Dev Mode</span>
              </button>
              
              {isConnected && (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle size={16} />
                  <span className="text-sm">Connected</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Welcome Banner */}
          {userInfo.name && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6"
            >
              <h2 className="text-2xl font-bold mb-2">
                Welcome back, {userInfo.name}! 👋
              </h2>
              <p className="opacity-90">
                We've transferred your conversation from {userInfo.platform}. 
                Let's continue where we left off!
              </p>
            </motion.div>
          )}

          {/* Messages */}
          <div className="h-96 overflow-y-auto p-6 space-y-4">
            {messages.map(msg => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, x: msg.sender === 'user' ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                  msg.sender === 'user' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <div className="flex items-start gap-2">
                    {msg.sender === 'bot' && <Bot size={16} className="mt-1" />}
                    <div className="flex-1">
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                      
                      {/* Action Buttons */}
                      {msg.buttons && msg.buttons.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {msg.buttons.map((button: MessageButton, idx: number) => (
                            <button
                              key={idx}
                              onClick={() => handleButtonClick(button)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105 ${
                                button.style === 'primary' 
                                  ? msg.sender === 'user'
                                    ? 'bg-purple-400 text-white hover:bg-purple-300'
                                    : 'bg-purple-500 text-white hover:bg-purple-600'
                                  : button.style === 'success'
                                  ? 'bg-green-500 text-white hover:bg-green-600'
                                  : msg.sender === 'user'
                                  ? 'bg-purple-400 text-white hover:bg-purple-300'
                                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                            >
                              {button.label}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className={`text-xs mt-1 flex items-center gap-1 ${
                        msg.sender === 'user' ? 'text-purple-100' : 'text-gray-500'
                      }`}>
                        <Clock size={10} />
                        {msg.timestamp.toLocaleTimeString()}
                        {msg.status && (
                          <span className="ml-1">
                            {msg.status === 'delivered' && '✓✓'}
                            {msg.status === 'sent' && '✓'}
                            {msg.status === 'sending' && '⏱'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Prompt */}
          {showWhatsAppPrompt && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-6 mb-4 p-4 bg-green-50 border border-green-200 rounded-xl"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <MessageCircle className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900">Connect WhatsApp for Better Support</p>
                    <p className="text-sm text-green-700">Get instant replies and 24/7 assistance</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowWhatsAppPrompt(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  type="tel"
                  placeholder="Your WhatsApp number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={connectWhatsApp}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                >
                  Connect <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="px-6 pb-3 suggestions-container">
              <p className="text-xs text-gray-500 mb-2">
                {language === 'pt' ? 'Sugestões:' : 'Suggestions:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInputMessage(suggestion)}
                    className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm hover:bg-purple-100 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={language === 'pt' ? 'Digite sua mensagem...' : 'Type your message...'}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim()}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Dev Mode Panel */}
        {showDevMode && latestAnalysis && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 bg-gray-900 text-green-400 rounded-xl p-6 font-mono text-sm overflow-hidden"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Brain className="text-purple-400" size={20} />
                AI Analysis Dashboard
              </h3>
              {isAIProcessing && (
                <span className="flex items-center gap-2 text-yellow-400">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                  {language === 'pt' ? 'Processando...' : 'Processing...'}
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Language:</span>
                  <span className="text-white font-semibold">
                    {latestAnalysis.language === 'pt' ? 'Portuguese' : 'English'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Intent:</span>
                  <span className="text-cyan-400 font-semibold">
                    {latestAnalysis.intent}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Confidence:</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full"
                        style={{ width: `${latestAnalysis.confidence * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-white font-semibold">
                      {(latestAnalysis.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Sentiment:</span>
                  <span className={`font-semibold ${
                    latestAnalysis.sentiment.label === 'positive' ? 'text-green-400' :
                    latestAnalysis.sentiment.label === 'negative' ? 'text-red-400' :
                    'text-yellow-400'
                  }`}>
                    {language === 'pt' 
                      ? (latestAnalysis.sentiment.label === 'positive' ? 'positivo' : 
                         latestAnalysis.sentiment.label === 'negative' ? 'negativo' : 'neutro')
                      : latestAnalysis.sentiment.label
                    } ({(latestAnalysis.sentiment.score * 100).toFixed(0)}%)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Processing:</span>
                  <span className="text-white font-semibold">
                    {latestAnalysis.processingTime}ms
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Entities:</span>
                  <span className="text-purple-400 font-semibold">
                    {latestAnalysis.entities.length} detected
                  </span>
                </div>
              </div>
            </div>
            
            {latestAnalysis.contextMatches && latestAnalysis.contextMatches.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-gray-400 mb-2">Context Matches (Vectorize):</p>
                <div className="space-y-1">
                  {latestAnalysis.contextMatches.slice(0, 2).map((match, idx) => (
                    <div key={idx} className="text-xs">
                      <span className="text-cyan-400">Match {idx + 1}:</span> 
                      <span className="text-gray-300 ml-2">{match.question}</span>
                      <span className="text-green-400 ml-2">({(match.score * 100).toFixed(0)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-4 pt-4 border-t border-gray-700 text-xs text-gray-500">
              Powered by CloudFlare AI • D1 Database • Vectorize Embeddings
            </div>
          </motion.div>
        )}
        
        {/* Platform Stats */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <Instagram className="text-pink-500" size={24} />
              <div>
                <p className="text-2xl font-bold">124</p>
                <p className="text-sm text-gray-500">{language === 'pt' ? 'Transferências Instagram' : 'Instagram Handoffs'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <MessageCircle className="text-green-500" size={24} />
              <div>
                <p className="text-2xl font-bold">89%</p>
                <p className="text-sm text-gray-500">{language === 'pt' ? 'Conversão WhatsApp' : 'WhatsApp Conversion'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <Bot className="text-purple-500" size={24} />
              <div>
                <p className="text-2xl font-bold flex items-center gap-1">
                  <Sparkles size={16} className="text-purple-400" />
                  AI
                </p>
                <p className="text-sm text-gray-500">{language === 'pt' ? 'Respostas Aprimoradas' : 'Enhanced Responses'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* AI Status Panel - Shows when Dev Mode is enabled */}
      {showDevMode && <AIStatus />}
      
      {/* AI Teacher - Interactive Tutorial */}
      <AITeacher currentPage="chat" />
    </div>
  );
};